/**
 * MIMOYA V1.1.1 Product Page Controller (assets/product.js)
 * (a) Gallery: thumbnails, arrows, swipe, expander.
 * (b) Variant picker: one radio group per product option, resolves the selected combination to a variant
 *     (price, URL ?variant=, gallery image, availability) and keeps a list of picked variants so several
 *     variations can be bought in one go ("Add another option").
 * (c) Quantity stepper for the current selection, add-button total, free-shipping meter.
 * Posts to /cart/add.js through theme.js initAjaxForms via hidden [data-variant-quantity] inputs.
 */

(function () {
  'use strict';

  function initPdp(root) {
    if (!root) return;
    const pdpRoot = (root.matches && root.matches('[data-pdp-root]')) ? root : root.querySelector('[data-pdp-root]');
    if (!pdpRoot) return;
    if (pdpRoot.dataset.pdpInitialized === 'true') return;
    pdpRoot.dataset.pdpInitialized = 'true';

    // Elements
    const mainMedia = pdpRoot.querySelector('[data-pdp-main-media]');
    const prevBtn = pdpRoot.querySelector('[data-pdp-media-prev]');
    const nextBtn = pdpRoot.querySelector('[data-pdp-media-next]');
    const thumbnailsContainer = pdpRoot.querySelector('[data-pdp-thumbnails]');
    const thumbnailBtns = Array.from(pdpRoot.querySelectorAll('[data-pdp-thumbnail-btn]'));
    const expandBtn = pdpRoot.querySelector('[data-pdp-thumbnails-expand]');

    const priceDisplay = pdpRoot.querySelector('[data-pdp-price]');
    const optionsRoot = pdpRoot.querySelector('[data-pdp-options]');
    const isMulti = !!optionsRoot;
    const variantsEl = pdpRoot.querySelector('[data-pdp-variants]');
    const groups = Array.from(pdpRoot.querySelectorAll('[data-option-group]'));

    const qtyInput = pdpRoot.querySelector('[data-pdp-qty-input]');
    const qtyDecBtn = pdpRoot.querySelector('[data-pdp-qty-dec]');
    const qtyIncBtn = pdpRoot.querySelector('[data-pdp-qty-inc]');
    const addAnotherBtn = pdpRoot.querySelector('[data-pdp-add-another]');
    const unavailableMsg = pdpRoot.querySelector('[data-pdp-unavailable]');
    const picksBox = pdpRoot.querySelector('[data-pdp-picks]');
    const picksList = pdpRoot.querySelector('[data-pdp-picks-list]');
    const pickTpl = pdpRoot.querySelector('[data-pdp-pick-template]');
    const pickInputs = pdpRoot.querySelector('[data-pdp-pick-inputs]');

    const submitBtn = pdpRoot.querySelector('[data-pdp-submit-btn]');
    const addLabelEl = pdpRoot.querySelector('[data-add-label]');

    const meterRoot = pdpRoot.querySelector('[data-pdp-free-shipping-meter]');
    const meterText = pdpRoot.querySelector('[data-meter-text]');
    const meterFill = pdpRoot.querySelector('[data-meter-fill]');

    // State
    let cartTotalCents = meterRoot ? parseInt(meterRoot.dataset.cartTotal || '0', 10) : 0;
    const thresholdCents = meterRoot
      ? parseInt(meterRoot.dataset.freeShippingThreshold || '0', 10)
      : (window.MIMOYA && window.MIMOYA.freeShippingThreshold) || 2500;
    let includeSelected = true;
    let pendingAdd = false;

    function formatMoneySafe(cents) {
      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(cents, window.MIMOYA && window.MIMOYA.moneyFormat);
      }
      return (cents / 100).toFixed(2);
    }

    // (a) Gallery Logic
    function showMedia(btn) {
      if (!btn || !mainMedia) return;
      const template = btn.querySelector('[data-pdp-media-template]');
      if (!template) return;

      mainMedia.innerHTML = '';
      mainMedia.appendChild(template.content.cloneNode(true));

      const mediaId = btn.dataset.pdpMediaId;
      thumbnailBtns.forEach((tb) => {
        const isActive = Boolean(mediaId) && String(tb.dataset.pdpMediaId) === String(mediaId);
        tb.classList.toggle('is-active', isActive);
        tb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    function cycleMedia(offset) {
      if (!thumbnailBtns.length) return;
      const currentIndex = thumbnailBtns.findIndex((b) => b.classList.contains('is-active'));
      const fromIndex = currentIndex === -1 ? 0 : currentIndex;
      const nextIndex = (fromIndex + offset + thumbnailBtns.length) % thumbnailBtns.length;
      showMedia(thumbnailBtns[nextIndex]);
    }

    thumbnailBtns.forEach((btn) => {
      btn.addEventListener('click', () => showMedia(btn));
    });

    if (prevBtn) prevBtn.addEventListener('click', () => cycleMedia(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => cycleMedia(1));

    // Touch swipe on main media
    if (mainMedia) {
      let touchStartX = 0;
      let touchStartY = 0;
      const SWIPE_THRESHOLD = 40;

      mainMedia.addEventListener('touchstart', (e) => {
        if (!e.touches || !e.touches[0]) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      mainMedia.addEventListener('touchend', (e) => {
        if (!e.changedTouches || !e.changedTouches[0]) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          cycleMedia(dx < 0 ? 1 : -1);
        }
      }, { passive: true });
    }

    // Thumbnail expander
    if (expandBtn && thumbnailsContainer) {
      const showAllLabel = (window.MIMOYA && window.MIMOYA.strings && window.MIMOYA.strings.show_all_photos) || '';
      const showFewerLabel = (window.MIMOYA && window.MIMOYA.strings && window.MIMOYA.strings.show_fewer_photos) || '';
      const expandTextEl = expandBtn.querySelector('[data-expand-text]');

      expandBtn.addEventListener('click', () => {
        const isExpanded = thumbnailsContainer.classList.toggle('is-expanded');
        expandBtn.setAttribute('aria-expanded', String(isExpanded));
        const newText = isExpanded ? showFewerLabel : showAllLabel;
        if (expandTextEl) expandTextEl.textContent = newText;
        expandBtn.setAttribute('aria-label', newText);
      });
    }

    // (b) Variant picker + (c) quantities ---------------------------------------------------------
    let variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) { variants = []; }
    }
    const variantById = (id) => variants.find((v) => String(v.id) === String(id)) || null;
    const maxOf = (v) => (v && v.x) || 99;
    const prettyValue = (val) => String(val || '').replace(/\s*:\s*/, ', ');

    let selection = groups.map((g) => {
      const r = g.querySelector('input[data-option-radio]:checked');
      return r ? r.value : '';
    });
    let current = null;
    let draftQty = 1;
    let picks = []; // committed selections: { id, qty }

    // Single-variant products keep one hidden id + the stepper
    const singleInput = pdpRoot.querySelector('[data-single-variant-id]');
    const singleVariant = singleInput ? {
      id: singleInput.value,
      p: parseInt(singleInput.dataset.price || '0', 10),
      c: singleInput.dataset.compareAtPrice ? parseInt(singleInput.dataset.compareAtPrice, 10) : 0,
      a: singleInput.dataset.available === 'true',
      x: 99
    } : null;

    function findCurrent() {
      return variants.find((v) => v.o.every((x, i) => x === selection[i])) || null;
    }

    function activeVariant() {
      return isMulti ? current : singleVariant;
    }

    function mergedItems() {
      const map = new Map();
      picks.forEach((p) => map.set(String(p.id), (map.get(String(p.id)) || 0) + p.qty));
      const cur = activeVariant();
      if (cur && cur.a && draftQty > 0) {
        const key = String(cur.id);
        map.set(key, Math.min(maxOf(cur), (map.get(key) || 0) + draftQty));
      }
      const out = [];
      map.forEach((qty, id) => {
        const v = isMulti ? variantById(id) : singleVariant;
        if (v && qty > 0) out.push({ id: id, qty: qty, v: v });
      });
      return out;
    }

    function markDirty() { includeSelected = true; }

    function renderPrice() {
      const cur = activeVariant();
      if (!priceDisplay || !cur) return;
      priceDisplay.textContent = '';
      if (cur.c && cur.c > cur.p) {
        const now = document.createElement('span');
        now.className = 'm-pdp-price__current';
        now.textContent = formatMoneySafe(cur.p);
        const was = document.createElement('s');
        was.className = 'm-pdp-price__compare';
        was.textContent = formatMoneySafe(cur.c);
        priceDisplay.appendChild(now);
        priceDisplay.appendChild(document.createTextNode(' '));
        priceDisplay.appendChild(was);
      } else {
        priceDisplay.textContent = formatMoneySafe(cur.p);
      }
    }

    function syncGroups() {
      groups.forEach((g, gi) => {
        const sel = selection[gi];
        const label = g.querySelector('[data-option-selected]');
        if (label) label.textContent = '— ' + prettyValue(sel);
        g.querySelectorAll('[data-option-radio]').forEach((r) => {
          r.checked = r.value === sel;
          const others = variants.filter((v) => v.o.every((x, i) => i === gi || x === selection[i]));
          const available = others.some((v) => v.o[gi] === r.value && v.a);
          const sw = r.closest('[data-swatch]');
          if (sw) sw.classList.toggle('is-unavailable', !available);
        });
      });
    }

    function syncUrl() {
      if (!current || !(window.history && window.history.replaceState)) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', current.id);
        window.history.replaceState({ path: url.toString() }, '', url.toString());
      } catch (e) { /* ignore */ }
    }

    function syncMedia() {
      if (!current || !current.m || !thumbnailBtns.length) return;
      const thumb = thumbnailBtns.find((b) => String(b.dataset.pdpMediaId) === String(current.m));
      if (thumb) showMedia(thumb);
    }

    function renderStepper() {
      if (!qtyInput) return;
      const cur = activeVariant();
      const usable = !!(cur && cur.a);
      const max = maxOf(cur);
      qtyInput.value = draftQty;
      qtyInput.disabled = !usable;
      if (qtyDecBtn) qtyDecBtn.disabled = !usable || draftQty <= (isMulti ? 0 : 1);
      if (qtyIncBtn) qtyIncBtn.disabled = !usable || draftQty >= max;
      if (addAnotherBtn) addAnotherBtn.disabled = !(usable && draftQty > 0);
      if (unavailableMsg) unavailableMsg.hidden = !(isMulti && (!current || !current.a));
    }

    function labelFor(tpl, v) { return (tpl || '').replace('[variant]', v.t); }

    function renderPicks() {
      if (!isMulti || !picksBox || !picksList || !pickTpl) return;
      picksList.textContent = '';
      picksBox.hidden = picks.length === 0;
      picks.forEach((p) => {
        const v = variantById(p.id);
        if (!v) return;
        const li = pickTpl.content.firstElementChild.cloneNode(true);
        li.querySelector('[data-pick-name]').textContent = v.t;
        li.querySelector('[data-pick-price]').textContent = formatMoneySafe(v.p * p.qty);
        const input = li.querySelector('[data-pick-input]');
        input.value = p.qty;
        input.max = maxOf(v);
        input.setAttribute('aria-label', labelFor(optionsRoot.dataset.labelQty, v));
        const dec = li.querySelector('[data-pick-dec]');
        const inc = li.querySelector('[data-pick-inc]');
        dec.setAttribute('aria-label', labelFor(optionsRoot.dataset.labelDec, v));
        inc.setAttribute('aria-label', labelFor(optionsRoot.dataset.labelInc, v));
        const rm = li.querySelector('[data-pick-remove]');
        rm.setAttribute('aria-label', labelFor(optionsRoot.dataset.labelRemove, v));
        const setQty = (q) => {
          markDirty();
          const next = Math.max(0, Math.min(maxOf(v), q));
          if (next === 0) picks = picks.filter((x) => String(x.id) !== String(p.id));
          else p.qty = next;
          renderAll();
        };
        dec.addEventListener('click', () => setQty(p.qty - 1));
        inc.addEventListener('click', () => setQty(p.qty + 1));
        input.addEventListener('change', () => setQty(parseInt(input.value, 10) || 0));
        rm.addEventListener('click', () => setQty(0));
        picksList.appendChild(li);
      });
    }

    function renderInputs(items) {
      if (!isMulti || !pickInputs) return;
      pickInputs.textContent = '';
      items.forEach((it) => {
        const inp = document.createElement('input');
        inp.type = 'hidden';
        inp.value = it.qty;
        inp.setAttribute('data-variant-quantity', '');
        inp.setAttribute('data-variant-id', it.id);
        pickInputs.appendChild(inp);
      });
    }

    function renderTotals() {
      const items = mergedItems();
      let totalCents = 0;
      let totalQty = 0;
      items.forEach((it) => { totalCents += it.v.p * it.qty; totalQty += it.qty; });
      renderInputs(items);

      if (submitBtn) {
        const cur = activeVariant();
        const anyAvailable = isMulti ? variants.some((v) => v.a) : !!(singleVariant && singleVariant.a);
        if (!anyAvailable) {
          submitBtn.disabled = true;
          if (addLabelEl) addLabelEl.textContent = (window.MIMOYA && window.MIMOYA.strings && window.MIMOYA.strings.sold_out) || '';
        } else {
          submitBtn.disabled = totalQty === 0;
          if (addLabelEl) {
            const addText = (window.MIMOYA && window.MIMOYA.strings && window.MIMOYA.strings.add_to_cart) || '';
            const tpl = submitBtn.dataset.addTemplate || (addText + ' [total]');
            const parts = tpl.split('[total]');
            const totalSpan = document.createElement('span');
            totalSpan.setAttribute('data-add-total', '');
            totalSpan.textContent = formatMoneySafe(totalCents);
            addLabelEl.textContent = parts[0];
            addLabelEl.appendChild(totalSpan);
            if (parts[1]) addLabelEl.appendChild(document.createTextNode(parts[1]));
          }
        }
        void cur;
      }

      if (meterRoot && meterText && meterFill) {
        const effective = cartTotalCents + (includeSelected ? totalCents : 0);
        const remaining = thresholdCents - effective;
        if (remaining > 0) {
          meterText.textContent = (meterRoot.dataset.msgRemaining || '').replace('[amount]', formatMoneySafe(remaining));
        } else {
          meterText.textContent = meterRoot.dataset.msgUnlocked || '';
        }
        let pct = thresholdCents > 0 ? Math.round((effective / thresholdCents) * 100) : 100;
        meterFill.style.width = Math.max(0, Math.min(100, pct)) + '%';
      }
    }

    function renderAll() {
      if (isMulti) {
        current = findCurrent();
        syncGroups();
      }
      renderPrice();
      renderStepper();
      renderPicks();
      renderTotals();
    }

    function onOptionChange(gi, value) {
      markDirty();
      selection[gi] = value;
      if (!findCurrent()) {
        // Combination does not exist: move the other options to the nearest real variant
        const alt = variants.find((v) => v.o[gi] === value && v.a) || variants.find((v) => v.o[gi] === value);
        if (alt) selection = alt.o.slice();
      }
      if (draftQty === 0) draftQty = 1;
      current = findCurrent();
      if (current) draftQty = Math.min(draftQty, maxOf(current));
      syncUrl();
      syncMedia();
      renderAll();
    }

    // Thumbnail groups: show one row, hide the rest behind the expander (row length follows the available width)
    function fitThumbRows() {
      groups.forEach((g) => {
        const grid = g.querySelector('.m-pdp-option__grid--img');
        if (!grid) return;
        const more = g.querySelector('[data-option-expand]');
        const swatches = Array.from(grid.querySelectorAll('[data-swatch]'));
        swatches.forEach((sw) => sw.classList.remove('is-extra'));
        const wasExpanded = g.classList.contains('is-expanded');
        g.classList.add('is-expanded');
        const firstTop = swatches.length ? swatches[0].offsetTop : 0;
        let perRow = swatches.length;
        for (let i = 0; i < swatches.length; i++) {
          if (swatches[i].offsetTop > firstTop + 2) { perRow = i; break; }
        }
        const overflow = swatches.length > perRow;
        swatches.forEach((sw, i) => sw.classList.toggle('is-extra', overflow && i >= perRow));
        if (more) more.hidden = !overflow;
        const selectedHidden = overflow && swatches.some((sw, i) => i >= perRow && sw.querySelector('input').checked);
        const open = overflow ? (wasExpanded || selectedHidden) : true;
        g.classList.toggle('is-expanded', open);
        if (more) {
          more.setAttribute('aria-expanded', String(open));
          const text = more.querySelector('[data-option-expand-text]');
          if (text) text.textContent = open ? more.dataset.labelFewer : more.dataset.labelMore;
        }
      });
    }

    if (isMulti) {
      if (groups.some((g) => g.querySelector('.m-pdp-option__grid--img'))) {
        fitThumbRows();
        if ('ResizeObserver' in window) {
          let lastW = 0;
          const ro = new ResizeObserver((entries) => {
            const w = Math.round(entries[0].contentRect.width);
            if (w !== lastW) { lastW = w; fitThumbRows(); }
          });
          groups.forEach((g) => { const wrap = g.querySelector('.m-pdp-option__wrap'); if (wrap && g.querySelector('.m-pdp-option__grid--img')) ro.observe(wrap); });
        } else {
          window.addEventListener('resize', fitThumbRows);
        }
      }
      groups.forEach((g, gi) => {
        g.querySelectorAll('[data-option-radio]').forEach((r) => {
          r.addEventListener('change', () => { if (r.checked) onOptionChange(gi, r.value); });
        });
        const more = g.querySelector('[data-option-expand]');
        if (more) {
          const text = more.querySelector('[data-option-expand-text]');
          more.addEventListener('click', () => {
            const open = g.classList.toggle('is-expanded');
            more.setAttribute('aria-expanded', String(open));
            if (text) text.textContent = open ? more.dataset.labelFewer : more.dataset.labelMore;
          });
        }
      });

      if (addAnotherBtn) {
        addAnotherBtn.addEventListener('click', () => {
          const cur = current;
          if (!cur || !cur.a || draftQty <= 0) return;
          markDirty();
          const existing = picks.find((p) => String(p.id) === String(cur.id));
          if (existing) existing.qty = Math.min(maxOf(cur), existing.qty + draftQty);
          else picks.push({ id: cur.id, qty: Math.min(maxOf(cur), draftQty) });
          draftQty = 0;
          renderAll();
        });
      }
    }

    if (qtyInput) {
      const minQty = isMulti ? 0 : 1;
      const setDraft = (q) => {
        markDirty();
        const cur = activeVariant();
        draftQty = Math.max(minQty, Math.min(maxOf(cur), q));
        renderAll();
      };
      if (qtyDecBtn) qtyDecBtn.addEventListener('click', () => setDraft(draftQty - 1));
      if (qtyIncBtn) qtyIncBtn.addEventListener('click', () => setDraft(draftQty + 1));
      qtyInput.addEventListener('change', () => setDraft(parseInt(qtyInput.value, 10) || minQty));
    }

    const addForm = pdpRoot.querySelector('form[action*="/cart/add"]');
    if (addForm) addForm.addEventListener('submit', () => { pendingAdd = true; });

    // After a successful add the cart event arrives: start fresh and stop double counting in the meter
    if (window.__mimoyaPdpCartHandler) document.removeEventListener('mimoya:cart:updated', window.__mimoyaPdpCartHandler);
    window.__mimoyaPdpCartHandler = (event) => {
      if (event && event.detail && event.detail.cart && typeof event.detail.cart.total_price !== 'undefined') {
        cartTotalCents = event.detail.cart.total_price;
        if (pendingAdd) {
          includeSelected = false;
          pendingAdd = false;
          picks = [];
          draftQty = 1;
        }
        if (meterRoot) meterRoot.dataset.cartTotal = String(cartTotalCents);
        renderAll();
      }
    };
    document.addEventListener('mimoya:cart:updated', window.__mimoyaPdpCartHandler);

    // Initial state
    if (isMulti) {
      current = findCurrent();
      if (current && current.a) draftQty = Math.min(1, maxOf(current));
    }
    renderAll();
  }

  // Lifecycle listeners
  // Wait for DOMContentLoaded so theme.js (loaded later in the body) has defined formatMoney;
  // 'load' is a fallback for late execution (initPdp guards against double init).
  if (document.readyState === 'complete') {
    initPdp(document);
  } else {
    document.addEventListener('DOMContentLoaded', () => initPdp(document));
    window.addEventListener('load', () => initPdp(document));
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.querySelector('[data-pdp-root]')) {
      initPdp(event.target);
    }
  });
})();
